import { useState, useRef, useEffect } from "react";
import { useOrganization } from "../context/OrganizationContext";
import "./OrganizationSwitcher.css";

/**
 * OrganizationSwitcher - Dropdown for switching between organizations
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.7, 12.1, 12.7
 */
export function OrganizationSwitcher() {
  const {
    organizations,
    activeOrganization,
    isLoading,
    switchOrganization,
    error
  } = useOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  const handleSwitchOrganization = async (orgId: string) => {
    if (isSwitching || orgId === activeOrganization?.id) return;
    
    try {
      setIsSwitching(true);
      setIsOpen(false);
      await switchOrganization(orgId);
    } catch (err) {
      console.error('Failed to switch organization:', err);
    } finally {
      setIsSwitching(false);
    }
  };

  // No switcher needed when user only belongs to one org
  if (!error && !isLoading && organizations.length <= 1) {
    return (
      <div className="topbar-user">
        <span className="topbar-org-name-static">
          {activeOrganization?.name || 'Organization'}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="topbar-user" ref={containerRef}>
        <button
          className="topbar-avatar-btn topbar-org-switcher--error"
          onClick={() => window.location.reload()}
          title={`Error: ${error}. Click to reload.`}
        >
          <span className="topbar-avatar topbar-avatar--error">!</span>
          <span className="topbar-username">Error</span>
        </button>
      </div>
    );
  }

  return (
    <div className="topbar-user" ref={containerRef}>
      <button
        className="topbar-avatar-btn"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading || isSwitching}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {isLoading || isSwitching ? (
          <span className="topbar-avatar topbar-org-spinner" />
        ) : (
          <>
            <span className="topbar-avatar">
              {activeOrganization?.name?.charAt(0)?.toUpperCase() || 'O'}
            </span>
            <span className="topbar-username">
              {activeOrganization?.name || 'Organization'}
            </span>
            <span className="topbar-caret">▾</span>
          </>
        )}
      </button>

      {isOpen && (
        <div className="topbar-dropdown">
          <div className="topbar-dropdown-header">
            <div className="topbar-dropdown-label">Your Organizations</div>
          </div>
          <div className="topbar-dropdown-divider" />
          <div className="topbar-org-dropdown-list">
            {organizations.map((org) => (
              <button
                key={org.id}
                onClick={() => handleSwitchOrganization(org.id)}
                className={`topbar-dropdown-item topbar-org-item ${
                  org.id === activeOrganization?.id ? 'topbar-org-item--active' : ''
                }`}
                disabled={isSwitching}
              >
                <span className="topbar-org-item-name">{org.name}</span>
                <span className="topbar-org-item-role">{org.role.toLowerCase()}</span>
              </button>
            ))}
          </div>

          {organizations.length === 0 && !isLoading && (
            <div className="topbar-dropdown-header">
              <div className="topbar-dropdown-label">No organizations found</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
